import { useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { GameSet as GameSetModel } from "../../../models/gameSet";
import { Item as ItemModel } from "../../../models/item";
import * as GameSetsApi from "../../../network/gameSets_api";
import * as BadgesApi from "../../../network/collectibles_api";
import { GameSetInput } from "../../../network/gameSets_api";
import AddEditGameSetItems from "./AddEditGameSetItems";
import GameSetInputField from "../../../components/FormFields/GameSetInputField";
import { useTranslation } from "react-i18next";
import ActionButton from "../../../components/Buttons/Action/ActionButton";
import Divider from "../../../components/Decors/Divider/Divider";
import BadgeModal from "../../../components/Modals/BadgeModal";

interface AddEditGameSetProps {
  gameSetToEdit?: GameSetModel;
  onDismiss: () => void;
  onGameSetSaved: (gameSet: GameSetModel) => void;
}

const AddEditGameSet = ({
  gameSetToEdit,
  onDismiss,
  onGameSetSaved,
}: AddEditGameSetProps) => {
  const [currentGameSetToEdit, setCurrentGameSetToEdit] =
    useState<GameSetModel>();
  const [items, setItems] = useState<ItemModel[]>([]);
  const [titleToAddFirst, setTitleToAddFirst] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [newBadge, setNewBadge] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<GameSetInput>({
    defaultValues: {
      title: gameSetToEdit?.title || "",
    },
  });

  async function onGameSetSubmit(input: GameSetInput) {
    try {
      let gameSetResponse: GameSetModel;
      if (gameSetToEdit) {
        const newData: GameSetInput = {
          title: input.title,
          score: gameSetToEdit.score,
        };
        gameSetResponse = await GameSetsApi.updateGameSet(
          gameSetToEdit._id,
          newData
        );
      } else {
        gameSetResponse = await GameSetsApi.createGameSet(input);
        navigate(`/gameSets/${gameSetResponse._id}`);
        setTitleToAddFirst(true);
      }
      setCurrentGameSetToEdit(gameSetResponse);
      onGameSetSaved(gameSetResponse);
    } catch (error) {
      console.error(error);
      alert(error);
    }
  }

  /**
   * Checks if the user has the "First Fragment" badge.
   * If not, creates it and shows the badge modal.
   *
   * @returns {Promise<void>} Resolves when the badge check and creation process is complete.
   */
  async function checkUserHasFirstFragmentBadge(): Promise<void> {
    try {
      const badges = await BadgesApi.fetchBadges();
      const hasFirstFragment = badges.some(
        (badge) => badge.fileName === "firstFragment"
      );
      if (!hasFirstFragment) {
        await BadgesApi.createBadge({
          fileName: "firstFragment",
          englishTitle: "First Fragment",
          hungarianTitle: "Első töredék",
          englishRequirement: "Create yout first gameset",
          hungarianRequirement: "Készítsd el az első játékkészleted",
        });
      }
      setNewBadge("firstFragment");
      setShowBadgeModal(true);
    } catch (error) {
      console.error("Error updating badge:", error);
    }
  }

  /**
   * Handles the form submission and passes the form data to `onGameSetSubmit`.
   *
   * @param {GameSetInput} data - The form data submitted by the user.
   */
  const handleFormSubmit: SubmitHandler<GameSetInput> = (data) => {
    checkUserHasFirstFragmentBadge();
    onGameSetSubmit(data);
  };

  return (
    <Container>
      <BadgeModal
        newBadge={newBadge}
        show={showBadgeModal}
        onDismiss={() => setShowBadgeModal(false)}
      />
      <h1>
        {gameSetToEdit
          ? `${t("gameSet.editGameSet")}`
          : `${t("gameSet.newGameSet")}`}
      </h1>
      <Divider />
      <h2 className={"lightText"}>{t("gameSet.title")}</h2>
      <Form id="addEditGameSetForm" onSubmit={handleSubmit(handleFormSubmit)}>
        <Row className={`${"mb-3"} ${"flexCenter"}`}>
          <Col md={10}>
            <GameSetInputField
              name="title"
              label={t("gameSet.title")}
              type="text"
              placeholder={t("gameSet.title")}
              register={register}
              registerOptions={{ required: "Required" }}
              error={errors.title}
            />
          </Col>
          <Col md={1}>
            <ActionButton
              type="submit"
              form="addEditGameSetForm"
              id="button-addon2"
              disabled={isSubmitting}
            >
              <FaCheck />
            </ActionButton>
          </Col>
        </Row>
      </Form>
      {(gameSetToEdit?.title || titleToAddFirst) && (
        <>
          <AddEditGameSetItems
            onItemSaved={(newItem) => {
              setItems([...items, newItem]);
            }}
          />
        </>
      )}
      <div className={"button-container"}>
        <ActionButton
          onClick={() => {
            onDismiss();
            navigate(`/gameSets`);
          }}
        >
          {t("common.close")}
        </ActionButton>
      </div>
    </Container>
  );
};

export default AddEditGameSet;
